// components/DashboardHome.js - НОВОГОДНЯЯ ВЕРСИЯ с оптимизацией загрузки и статистикой доходов

import { useState, useEffect } from 'react';
import { TrendingUp, ArrowRight, ChevronLeft, ChevronRight, User, Users, Wallet, Copy, Share2, Check, UserPlus, Award, Target, Calendar, MessageCircle, Bell, ExternalLink, Clock, RefreshCw, Gift, Snowflake } from 'lucide-react';
import { Link } from 'react-router-dom';
import { authService } from '../services/authService';
import { dashboardService } from '../services/dashboardService';
import TelegramIntegration from './TelegramIntegration';
import { useAuth } from '../contexts/AuthContext';

const DashboardHome = () => {
    const { user: authUser, loading: authLoading } = useAuth();
    const [userData, setUserData] = useState(null);
    const [referralLink, setReferralLink] = useState('');
    const [copied, setCopied] = useState(false);
    const [showSnow, setShowSnow] = useState(true);
    const [dashboardStats, setDashboardStats] = useState({
        totalIncome: {
            lastMonthUsd: 0,
            totalUsd: 0,
            growth: 0
        },
        partnersInfo: {
            total: 0,
            active: 0,
            totalTeam: null,
            recentPartners: []
        }
    });
    const [isLoading, setIsLoading] = useState(true);
    const [statsLoading, setStatsLoading] = useState(false);
    const [incomeLoading, setIncomeLoading] = useState(false);
    const [currentSlide, setCurrentSlide] = useState(0);

    // Новогодние полезные ссылки
    const usefulLinks = [
        { id: 1, title: '🎄 Праздничный чат сообщества', url: 'https://t.me/+EWbDe4ZUdmRlMWJi', icon: 'telegram' },
        { id: 2, title: '🎁 Новости и акции', url: 'https://t.me/+QIkrGiBoYohiNDBi', icon: 'telegram' },
        { id: 3, title: '💸 Чеки', url: 'https://t.me/+l9Z_C674a285YWYy', icon: 'telegram' },
    ];

    // Новогодние новости
    const news = [
        {
            id: 1,
            title: '🎄 С Новым 2026 Годом!',
            content: 'Команда ЛАЙТ поздравляет всех партнёров с наступающими праздниками! Желаем успехов и процветания!',
            date: '2026-01-01',
            festive: true
        },
        {
            id: 2,
            title: '🎁 Новогодняя акция',
            content: 'Особые бонусы для новых партнёров до 15 января! Приглашайте друзей и получайте подарки!',
            date: '2026-01-05',
            festive: true
        },
        {
            id: 3,
            title: '⭐ Итоги 2025 года',
            content: 'Подводим итоги уходящего года. Благодарим всех за доверие и поддержку!',
            date: '2025-12-28',
            festive: true
        },
        {
            id: 4,
            title: '🆕 Запуск нового направления Light Miks',
            content: 'Мы рады объявить о скором запуске нового направления в рамках нашей платформы.\n' +
                'Примерная дата запуска: начало 2026 года.',
            date: '2026-01-30',
            festive: true
        }
    ];

    // Быстрая загрузка основных данных
    useEffect(() => {
        if (authUser) {
            console.log('✅ Быстрая загрузка основных данных из AuthContext');

            const teamStats = authUser.team_statistics;

            setUserData(authUser);
            setReferralLink(authUser.referral_link || '');

            setDashboardStats({
                totalIncome: {
                    lastMonthUsd: authUser.total_earned_usd || 0,
                    totalUsd: authUser.total_earned_usd || 0,
                    growth: authUser.income_growth || 0
                },
                partnersInfo: {
                    total: authUser.partners_count || 0,
                    active: authUser.active_partners_count || 0,
                    totalTeam: null,
                    recentPartners: teamStats?.recent_partners || []
                }
            });

            setIsLoading(false);
        }
    }, [authUser]);

    // Загрузка полной статистики команды
    useEffect(() => {
        if (authUser && !isLoading) {
            const timer = setTimeout(() => {
                fetchFullTeamStats();
            }, 300);

            return () => clearTimeout(timer);
        }
    }, [authUser, isLoading]);

    // Загрузка статистики доходов
    useEffect(() => {
        if (authUser && !isLoading) {
            const timer = setTimeout(() => {
                fetchIncomeStats();
            }, 500);

            return () => clearTimeout(timer);
        }
    }, [authUser, isLoading]);

    const fetchFullTeamStats = async () => {
        try {
            setStatsLoading(true);
            console.log('🔄 Загрузка полной статистики команды...');

            const response = await authService.getTeamStats();

            if (response.success) {
                const teamStats = response.data;

                setDashboardStats(prev => ({
                    ...prev,
                    partnersInfo: {
                        ...prev.partnersInfo,
                        totalTeam: teamStats.all_team?.total || 0,
                        recentPartners: teamStats.recent_partners || prev.partnersInfo.recentPartners
                    }
                }));

                console.log('✅ Полная статистика команды загружена');
            } else {
                console.error('❌ Ошибка загрузки статистики:', response.message);
                setDashboardStats(prev => ({
                    ...prev,
                    partnersInfo: {
                        ...prev.partnersInfo,
                        totalTeam: prev.partnersInfo.total
                    }
                }));
            }
        } catch (err) {
            console.error('❌ Ошибка загрузки статистики команды:', err);
            setDashboardStats(prev => ({
                ...prev,
                partnersInfo: {
                    ...prev.partnersInfo,
                    totalTeam: prev.partnersInfo.total
                }
            }));
        } finally {
            setStatsLoading(false);
        }
    };

    const fetchIncomeStats = async () => {
        try {
            setIncomeLoading(true);
            console.log('💰 Загрузка статистики доходов из FinancialOperation...');

            const token = localStorage.getItem('auth_token');
            const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

            const response = await fetch(`${API_URL}/dashboard/stats`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                throw new Error('Ошибка загрузки статистики');
            }

            const data = await response.json();

            if (data.success && data.data.income_statistics) {
                const incomeStats = data.data.income_statistics;

                setDashboardStats(prev => ({
                    ...prev,
                    totalIncome: {
                        lastMonthRub: incomeStats.last_month_income_rub || 0,
                        totalRub: incomeStats.total_income_rub || 0,
                        lastMonthUsd: incomeStats.last_month_income_usd || 0,
                        totalUsd: incomeStats.total_income_usd || 0,
                        growth: incomeStats.growth_percentage || 0
                    }
                }));

                console.log('✅ Статистика доходов загружена:', {
                    totalRub: incomeStats.total_income_rub,
                    totalUsd: incomeStats.total_income_usd,
                    growth: incomeStats.growth_percentage,
                    operations: incomeStats.total_operations
                });
            }
        } catch (err) {
            console.error('❌ Ошибка загрузки статистики доходов:', err);
        } finally {
            setIncomeLoading(false);
        }
    };

    const refreshTeamStats = () => {
        setDashboardStats(prev => ({
            ...prev,
            partnersInfo: {
                ...prev.partnersInfo,
                totalTeam: null
            }
        }));
        fetchFullTeamStats();
    };

    const refreshIncomeStats = () => {
        setIncomeLoading(true);
        fetchIncomeStats();
    };

    const copyReferralLink = () => {
        if (referralLink) {
            navigator.clipboard.writeText(referralLink);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    const nextSlide = () => {
        setCurrentSlide((prev) => (prev + 1) % news.length);
    };

    const prevSlide = () => {
        setCurrentSlide((prev) => (prev - 1 + news.length) % news.length);
    };

    const formatCurrency = (amount) => {
        if (!amount || amount === 0) {
            return '0.00';
        }

        return amount.toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    };

    const getIconComponent = (iconName) => {
        switch (iconName) {
            case 'telegram':
                return <MessageCircle size={16} className="text-blue-500" />;
            case 'vk':
                return <Users size={16} className="text-blue-600" />;
            default:
                return <ExternalLink size={16} className="text-gray-500" />;
        }
    };

    if (authLoading || isLoading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <div className="text-6xl mb-4 animate-bounce">🎄</div>
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Загружаем праздничную атмосферу...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6 relative">

            {/* ❄️ Снежинки в Dashboard ❄️ */}
            {showSnow && (
                <div className="fixed inset-0 pointer-events-none z-10 overflow-hidden">
                    {[...Array(30)].map((_, i) => (
                        <div
                            key={i}
                            className="absolute text-blue-200 animate-snowfall opacity-60"
                            style={{
                                left: `${Math.random() * 100}%`,
                                animationDuration: `${5 + Math.random() * 10}s`,
                                animationDelay: `${Math.random() * 5}s`,
                                fontSize: `${8 + Math.random() * 12}px`,
                            }}
                        >
                            ❄
                        </div>
                    ))}
                </div>
            )}

            {/* 🎄 НОВОГОДНЕЕ ПРИВЕТСТВИЕ 🎄 */}
            <div className="bg-gradient-to-r from-red-600 via-green-600 to-red-600 rounded-xl text-white p-6 relative overflow-hidden shadow-lg">
                {/* Декоративные снежинки */}
                <div className="absolute top-2 right-4 text-4xl opacity-20 animate-spin-slow">❄️</div>
                <div className="absolute bottom-2 left-4 text-3xl opacity-20">🎄</div>
                <div className="absolute top-1/2 right-1/4 text-2xl opacity-20">⭐</div>

                <div className="flex items-center justify-between relative z-10">
                    <div>
                        <div className="flex items-center gap-2 mb-2">
                            <span className="text-3xl">🎅</span>
                            <h1 className="text-2xl font-bold">
                                С Новым Годом, {userData?.first_name || userData?.login || 'Партнёр'}!
                            </h1>
                            <span className="text-3xl">🎄</span>
                        </div>
                        <p className="text-white/90">
                            🎁 Желаем успехов и процветания в 2026 году! ⭐
                        </p>
                    </div>
                    <div className="hidden md:block">
                        <div className="w-24 h-24 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                            {userData?.avatar ? (
                                <div className="relative">
                                    <img
                                        src={userData.avatar}
                                        alt="Аватар"
                                        className="w-20 h-20 rounded-full object-cover"
                                    />
                                    <div className="absolute -top-4 -right-2 text-3xl transform rotate-12">🎅</div>
                                </div>
                            ) : (
                                <div className="relative">
                                    <User size={32} className="text-white" />
                                    <div className="absolute -top-4 -right-2 text-2xl">🎅</div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Кнопка вкл/выкл снега */}
                <button
                    onClick={() => setShowSnow(!showSnow)}
                    className="absolute top-2 right-2 p-2 bg-white/20 rounded-full hover:bg-white/30 transition-colors"
                    title={showSnow ? "Выключить снег" : "Включить снег"}
                >
                    {showSnow ? "❄️" : "☀️"}
                </button>
            </div>

            {/* 🎁 НОВОГОДНЯЯ СТАТИСТИКА - 4 карточки 🎁 */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">

                {/* Общие доходы - Зелёная (ёлочная) карточка */}
                <div className="bg-gradient-to-br from-green-50 to-emerald-100 rounded-xl shadow-lg border-2 border-green-300 p-6 hover:shadow-xl transition-all hover:-translate-y-1 relative overflow-hidden">
                    <div className="absolute top-2 right-2 text-2xl opacity-30">🎄</div>
                    <div className="flex items-center justify-between mb-4">
                        <div className="p-3 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl shadow-md">
                            <TrendingUp className="text-white" size={24} />
                        </div>
                        <div className="flex items-center gap-2">
                            {incomeLoading && (
                                <Clock className="text-green-600 animate-pulse" size={16} />
                            )}
                            {!incomeLoading && dashboardStats.totalIncome.growth !== 0 && (
                                <span className={`text-xs font-bold px-2 py-1 rounded-full ${
                                    dashboardStats.totalIncome.growth > 0
                                        ? 'bg-green-200 text-green-800'
                                        : 'bg-red-200 text-red-800'
                                }`}>
                                    {dashboardStats.totalIncome.growth > 0 ? '↑' : '↓'}
                                    {Math.abs(dashboardStats.totalIncome.growth).toFixed(1)}%
                                </span>
                            )}
                            {!incomeLoading && (
                                <button
                                    onClick={refreshIncomeStats}
                                    className="p-1 hover:bg-white/80 rounded-full transition-colors"
                                    title="Обновить статистику"
                                >
                                    <RefreshCw size={14} className="text-gray-600" />
                                </button>
                            )}
                        </div>
                    </div>
                    <h3 className="text-sm font-semibold text-gray-600 mb-2">🎁 Общие доходы</h3>
                    {incomeLoading ? (
                        <div className="space-y-2">
                            <div className="h-8 w-24 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded animate-pulse"></div>
                            <div className="h-4 w-20 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded animate-pulse"></div>
                        </div>
                    ) : (
                        <>
                            <p className="text-3xl font-bold text-gray-900 mb-1">
                                {formatCurrency(dashboardStats.totalIncome.totalUsd)}
                                <span className="text-xl font-semibold text-green-600 ml-2">USDT</span>
                            </p>
                            {dashboardStats.totalIncome.lastMonthUsd > 0 && (
                                <p className="text-xs text-gray-500">
                                    За месяц: {formatCurrency(dashboardStats.totalIncome.lastMonthUsd)} USDT
                                </p>
                            )}
                        </>
                    )}
                </div>

                {/* Прямые партнёры - Красная (рождественская) карточка */}
                <div className="bg-gradient-to-br from-red-50 to-rose-100 rounded-xl shadow-lg border-2 border-red-300 p-6 hover:shadow-xl transition-all hover:-translate-y-1 relative overflow-hidden">
                    <div className="absolute top-2 right-2 text-2xl opacity-30">🎅</div>
                    <div className="flex items-center justify-between mb-4">
                        <div className="p-3 bg-gradient-to-br from-red-500 to-rose-600 rounded-xl shadow-md">
                            <UserPlus className="text-white" size={24} />
                        </div>
                        {dashboardStats.partnersInfo.active > 0 && (
                            <div className="flex items-center gap-1 px-2 py-1 bg-white/80 rounded-full">
                                <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></div>
                                <span className="text-xs font-medium text-gray-700">{dashboardStats.partnersInfo.active} активных</span>
                            </div>
                        )}
                    </div>
                    <h3 className="text-sm font-semibold text-gray-600 mb-2">🎄 Прямые партнёры</h3>
                    <p className="text-3xl font-bold text-gray-900 mb-1">
                        {dashboardStats.partnersInfo.total}
                        <span className="text-xl font-semibold text-red-600 ml-2">чел.</span>
                    </p>
                    <p className="text-xs text-gray-500">
                        Ваши рефералы
                    </p>
                </div>

                {/* Общая команда - Золотая карточка */}
                <div className="bg-gradient-to-br from-yellow-50 to-amber-100 rounded-xl shadow-lg border-2 border-yellow-300 p-6 hover:shadow-xl transition-all hover:-translate-y-1 relative overflow-hidden">
                    <div className="absolute top-2 right-2 text-2xl opacity-30">⭐</div>
                    <div className="flex items-center justify-between mb-4">
                        <div className="p-3 bg-gradient-to-br from-yellow-500 to-amber-600 rounded-xl shadow-md">
                            <Users className="text-white" size={24} />
                        </div>
                        {statsLoading && dashboardStats.partnersInfo.totalTeam === null && (
                            <Clock className="text-yellow-600 animate-pulse" size={16} />
                        )}
                    </div>
                    <h3 className="text-sm font-semibold text-gray-600 mb-2">⭐ Общая команда</h3>
                    {statsLoading && dashboardStats.partnersInfo.totalTeam === null ? (
                        <div className="space-y-2">
                            <div className="h-8 w-20 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded animate-pulse"></div>
                            <div className="h-4 w-32 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded animate-pulse"></div>
                        </div>
                    ) : (
                        <>
                            <p className="text-3xl font-bold text-gray-900 mb-1">
                                {dashboardStats.partnersInfo.totalTeam || 0}
                                <span className="text-xl font-semibold text-yellow-600 ml-2">чел.</span>
                            </p>
                            <p className="text-xs text-gray-500">
                                Все уровни структуры
                            </p>
                        </>
                    )}
                </div>

                {/* Баланс - Синяя (зимняя) карточка */}
                <div className="bg-gradient-to-br from-blue-50 to-cyan-100 rounded-xl shadow-lg border-2 border-blue-300 p-6 hover:shadow-xl transition-all hover:-translate-y-1 relative overflow-hidden">
                    <div className="absolute top-2 right-2 text-2xl opacity-30">❄️</div>
                    <div className="flex items-center justify-between mb-4">
                        <div className="p-3 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-xl shadow-md">
                            <Wallet className="text-white" size={24} />
                        </div>
                        <div className="flex items-center gap-1 px-3 py-1 bg-white/80 rounded-full">
                            <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse"></div>
                            <span className="text-xs font-medium text-gray-700">Доступно</span>
                        </div>
                    </div>
                    <h3 className="text-sm font-semibold text-gray-600 mb-2">❄️ Ваш баланс</h3>
                    <p className="text-3xl font-bold text-gray-900 mb-1">
                        {formatCurrency(userData?.balance_usd || 0)}
                        <span className="text-xl font-semibold text-blue-600 ml-2">USDT</span>
                    </p>
                    <p className="text-xs text-gray-500">
                        Криптовалюта BEP-20
                    </p>
                </div>
            </div>

            {/* Основной контент */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Левая колонка */}
                <div className="lg:col-span-2 space-y-6">

                    {/* 🎁 НОВОГОДНЯЯ РЕФЕРАЛЬНАЯ ССЫЛКА 🎁 */}
                    <div className="bg-gradient-to-r from-red-50 via-white to-green-50 rounded-xl shadow-sm border-2 border-dashed border-red-300 p-6 relative overflow-hidden">
                        <div className="absolute top-2 left-2 text-2xl">🎁</div>
                        <div className="absolute top-2 right-2 text-2xl">🎄</div>

                        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center justify-center">
                            <Gift className="mr-2 text-red-500" size={20} />
                            🎅 Новогодний подарок для друзей! 🎅
                            <Gift className="ml-2 text-green-500" size={20} />
                        </h3>
                        <p className="text-sm text-gray-600 mb-4 text-center">
                            Поделитесь праздничным приглашением и получите бонусы за каждого партнёра! ⭐
                        </p>
                        <div className="flex gap-2">
                            <input
                                type="text"
                                value={referralLink}
                                readOnly
                                className="flex-1 px-4 py-3 border-2 border-green-300 rounded-lg bg-white text-sm font-medium"
                            />
                            <button
                                onClick={copyReferralLink}
                                className="px-6 py-3 bg-gradient-to-r from-red-500 to-green-500 text-white rounded-lg hover:from-red-600 hover:to-green-600 transition-all flex items-center gap-2 font-bold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                            >
                                {copied ? (
                                    <>
                                        <Check size={18} />
                                        🎉 Скопировано!
                                    </>
                                ) : (
                                    <>
                                        <Copy size={18} />
                                        🎁 Копировать
                                    </>
                                )}
                            </button>
                        </div>
                    </div>

                    {/* Последние партнёры */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                                🎄 Последние партнёры
                            </h3>
                            <Link
                                to="/dashboard/partners"
                                className="text-sm text-green-600 hover:underline flex items-center"
                            >
                                Все партнёры
                                <ArrowRight size={14} className="ml-1" />
                            </Link>
                        </div>

                        {dashboardStats.partnersInfo.recentPartners.length > 0 ? (
                            <div className="space-y-3">
                                {dashboardStats.partnersInfo.recentPartners.slice(0, 5).map((partner, index) => (
                                    <div key={partner.id} className="flex items-center justify-between py-3 px-4 bg-gradient-to-r from-green-50 to-red-50 rounded-lg border border-green-100 hover:shadow-md transition-all">
                                        <div className="flex items-center">
                                            {partner.avatar && partner.avatar !== false ? (
                                                <img
                                                    src={`https://api.light-platform.com/storage/${partner.avatar}`}
                                                    alt={partner.login}
                                                    className="w-10 h-10 rounded-full object-cover mr-3"
                                                    onError={(e) => {
                                                        e.target.style.display = 'none';
                                                        e.target.nextSibling.style.display = 'flex';
                                                    }}
                                                />
                                            ) : null}
                                            <div
                                                className={`w-10 h-10 bg-gradient-to-br from-red-400 to-green-400 rounded-full flex items-center justify-center mr-3 relative ${
                                                    partner.avatar && partner.avatar !== false ? 'hidden' : ''
                                                }`}
                                            >
                                                <span className="text-white text-sm font-semibold">
                                                    {partner.full_name?.split(' ').map(n => n[0]).join('') || partner.login?.[0] || 'P'}
                                                </span>
                                                {/* Маленькая ёлочка для новых */}
                                                {index === 0 && (
                                                    <span className="absolute -top-1 -right-1 text-sm">🎄</span>
                                                )}
                                            </div>
                                            <div>
                                                <p className="text-sm font-medium text-gray-900">
                                                    {partner.full_name || partner.login}
                                                </p>
                                                <p className="text-xs text-gray-500">
                                                    {partner.registration_date ? new Date(partner.registration_date).toLocaleDateString('ru-RU') : ''}
                                                </p>
                                            </div>
                                        </div>
                                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                                            partner.status === 'active'
                                                ? 'bg-green-100 text-green-800'
                                                : 'bg-gray-100 text-gray-800'
                                        }`}>
                                            {partner.status === 'active' ? '✅ Активен' : '⏳ Неактивен'}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-8 text-gray-500">
                                <div className="text-5xl mb-4">🎄</div>
                                <p className="text-sm">Пригласите друзей на праздник!</p>
                                <p className="text-xs">Поделитесь новогодней ссылкой выше 🎁</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Правая колонка */}
                <div className="space-y-6">
                    {/* Telegram интеграция */}
                    <TelegramIntegration user={userData} isProfilePage={false} />

                    {/* Праздничные ссылки */}
                    <div className="bg-gradient-to-br from-red-50 to-green-50 rounded-xl shadow-sm border-2 border-green-200 p-6">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                            🎄 Праздничные ссылки ⭐
                        </h3>
                        <div className="space-y-2">
                            {usefulLinks.map((link) => (
                                <a
                                    key={link.id}
                                    href={link.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center p-3 bg-white hover:bg-green-50 rounded-lg transition-colors group border border-green-100"
                                >
                                    <span className="text-xl mr-2">🎁</span>
                                    <span className="text-sm text-gray-700 group-hover:text-green-700 flex-1 font-medium">
                                        {link.title}
                                    </span>
                                    <ExternalLink size={14} className="text-green-400 group-hover:text-green-600" />
                                </a>
                            ))}
                        </div>
                    </div>

                    {/* Праздничные новости */}
                    <div className="bg-gradient-to-br from-yellow-50 to-red-50 rounded-xl shadow-sm border-2 border-yellow-200 p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                                🎅 Новогодние новости 🎄
                            </h3>
                            <div className="flex gap-1">
                                <button
                                    onClick={prevSlide}
                                    className="p-1 rounded-full hover:bg-white transition-colors"
                                >
                                    <ChevronLeft size={16} className="text-gray-600" />
                                </button>
                                <button
                                    onClick={nextSlide}
                                    className="p-1 rounded-full hover:bg-white transition-colors"
                                >
                                    <ChevronRight size={16} className="text-gray-600" />
                                </button>
                            </div>
                        </div>

                        <div className="relative">
                            {news.length > 0 && (
                                <div className="space-y-3">
                                    <div className={`p-4 rounded-lg border ${
                                        currentSlide === 0
                                            ? 'bg-gradient-to-r from-red-100 to-green-100 border-red-200'
                                            : 'bg-white border-gray-100'
                                    }`}>
                                        <h4 className="font-semibold text-gray-800 mb-2 text-sm">
                                            {news[currentSlide].title}
                                        </h4>
                                        <p className="text-xs text-gray-600 mb-2">
                                            {news[currentSlide].content}
                                        </p>
                                        <div className="flex items-center text-xs text-gray-500">
                                            <Calendar size={12} className="mr-1" />
                                            {new Date(news[currentSlide].date).toLocaleDateString('ru-RU')}
                                        </div>
                                    </div>

                                    {/* Индикаторы слайдов */}
                                    <div className="flex justify-center space-x-1">
                                        {news.map((_, index) => (
                                            <button
                                                key={index}
                                                onClick={() => setCurrentSlide(index)}
                                                className={`w-2 h-2 rounded-full transition-colors ${
                                                    index === currentSlide
                                                        ? 'bg-red-500'
                                                        : 'bg-gray-300 hover:bg-gray-400'
                                                }`}
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Быстрые действия */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                            ⭐ Быстрые действия
                        </h3>

                        <div className="space-y-3">
                            <Link
                                to="/dashboard/profile"
                                className="flex items-center p-3 bg-gradient-to-r from-green-50 to-red-50 hover:from-green-100 hover:to-red-100 rounded-lg transition-colors group border border-green-100"
                            >
                                <User className="text-green-600 mr-3" size={16} />
                                <span className="text-gray-700 group-hover:text-gray-900 transition-colors">
                                    🎄 Редактировать профиль
                                </span>
                                <ArrowRight className="ml-auto text-green-400 group-hover:text-green-600 transition-colors" size={14} />
                            </Link>

                            <Link
                                to="/dashboard/partners"
                                className="flex items-center p-3 bg-gradient-to-r from-red-50 to-green-50 hover:from-red-100 hover:to-green-100 rounded-lg transition-colors group border border-red-100"
                            >
                                <Users className="text-red-600 mr-3" size={16} />
                                <span className="text-gray-700 group-hover:text-gray-900 transition-colors">
                                    🎅 Управление командой
                                </span>
                                <ArrowRight className="ml-auto text-red-400 group-hover:text-red-600 transition-colors" size={14} />
                            </Link>

                            <Link
                                to="/dashboard/finance"
                                className="flex items-center p-3 bg-gradient-to-r from-yellow-50 to-green-50 hover:from-yellow-100 hover:to-green-100 rounded-lg transition-colors group border border-yellow-100"
                            >
                                <Target className="text-yellow-600 mr-3" size={16} />
                                <span className="text-gray-700 group-hover:text-gray-900 transition-colors">
                                    ⭐ Финансы и выплаты
                                </span>
                                <ArrowRight className="ml-auto text-yellow-400 group-hover:text-yellow-600 transition-colors" size={14} />
                            </Link>
                        </div>
                    </div>
                </div>
            </div>

            {/* CSS для анимации снежинок */}
            <style>{`
                @keyframes snowfall {
                    0% {
                        transform: translateY(-10vh) rotate(0deg);
                    }
                    100% {
                        transform: translateY(100vh) rotate(360deg);
                    }
                }
                .animate-snowfall {
                    animation: snowfall linear infinite;
                }
                @keyframes spin-slow {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
                .animate-spin-slow {
                    animation: spin-slow 10s linear infinite;
                }
            `}</style>
        </div>
    );
};

export default DashboardHome;
